import React, {
  useState,
  useMemo,
  useReducer,
  useCallback,
  useEffect,
} from "react";

import { useSession } from "next-auth/react";

import { type IModel } from "@musicfy/libs/ModelsProvider/IModels";
import { type IFileUpload } from "@musicfy/utils/hooks/useStorage";

import AdvancedSettings from "./AdvancedSettings";
import InferenceButton from "./InferenceButton";
import MultiFileUploader from "./MultiFileUploader";
import OnboardingStep from "./OnboardingStep";
import ProcessingEffects from "./ProcessingEffects";
import SelectModel from "./SelectModel";
import {
  type OnboardingAction,
  type OnboardingState,
  DEFAULT_INFERENCE_SETTINGS,
  type TInferenceSettings,
} from "./types";
import FAQ from "../FAQ";

interface IRVCInferenceProps {
  initialSelectedModels?: IModel[];
}

const RVCInference: React.FC<IRVCInferenceProps> = ({
  initialSelectedModels = [],
}) => {
  const [selectedModels, setSelectedModels] = useState<IModel[]>(
    initialSelectedModels
  );
  const { update, status, data } = useSession();

  const reducer = useCallback(
    (state: OnboardingState, action: OnboardingAction) => {
      switch (action.type) {
        case "NEXT_STEP":
          return { ...state, currentStepIndex: state.currentStepIndex + 1 };
        case "PREVIOUS_STEP":
          return { ...state, currentStepIndex: state.currentStepIndex - 1 };
        case "OPEN_ONBOARDING":
          return { ...state, isOpen: true };
        case "CLOSE_ONBOARDING":
          return { ...state, isOpen: false, onboardingComplete: true };
        default:
          return state;
      }
    },
    []
  );

  const [onboardingState, dispatchOnboarding] = useReducer(reducer, {
    currentStepIndex: 0,
    isOpen: false,
    onboardingComplete: false,
  });

  useEffect(() => {
    if (status !== "authenticated") {
      return;
    }

    if (!!data.user.onboardingComplete) {
      return;
    }

    if (onboardingState.onboardingComplete) {
      update({ onboardingComplete: true });
      return;
    }

    if (!data?.user?.onboardingComplete && !onboardingState.isOpen) {
      dispatchOnboarding({ type: "OPEN_ONBOARDING" });
      return;
    }
  }, [
    status,
    data?.user.onboardingComplete,
    onboardingState.isOpen,
    update,
    onboardingState.onboardingComplete,
  ]);
  const [uploadedFiles, setUploadedFiles] = useState<IFileUpload[]>([]);
  const [inferenceSettings, setInferenceSettings] =
    useState<TInferenceSettings>(DEFAULT_INFERENCE_SETTINGS);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const QUESTIONS = useMemo(
    () => [
      {
        question: `How do I create ${
          selectedModels[0]?.artist || ""
        } AI Covers?`,
        answer: `To make a ${
          selectedModels[0]?.artist || ""
        } cover simply upload an audio clip or enter a link from youtube. If this audio has instrumentals open advanced settings and make sure remove instrumentals is toggled on.`,
      },
      {
        question: `How can I make my AI ${
          selectedModels[0]?.artist || ""
        } cover sound better?`,
        answer: `In order to get the best audio results you should upload an audio clip that is purely an acapella.`,
      },
      {
        question: `Do I own all licensing and copyright of the ${
          selectedModels[0]?.artist || ""
        } AI cover?`,
        answer:
          !selectedModels[0]?.isCommunityModel || ""
            ? `Yes, if you are on a paid plan you own all rights to the audio.`
            : `No, you do not own commercial rights to the audio. You can use the audio for personal use only.`,
      },
    ],
    [selectedModels]
  );

  return (
    <>
      <div className="relative z-10 m-auto box-border">
        <SelectModel
          selectedModels={selectedModels}
          setSelectedModels={setSelectedModels}
          dispatchOnboarding={dispatchOnboarding}
          onboardingState={onboardingState}
        />
        <OnboardingStep
          onClose={() => {
            dispatchOnboarding({ type: "CLOSE_ONBOARDING" });
          }}
          isOpen={
            onboardingState.isOpen && onboardingState.currentStepIndex === 1
          }
          content="Upload, record, or link your audio"
          position="top"
        >
          <div className="mt-6">
            <MultiFileUploader
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              setIsUploading={setIsUploading}
              onboardingState={onboardingState}
              dispatchOnboarding={dispatchOnboarding}
            />
          </div>
        </OnboardingStep>
        <div className="w-full mt-6 flex lg:flex-row flex-col gap-5">
          <ProcessingEffects
            inferenceSettings={inferenceSettings}
            setInferenceSettings={setInferenceSettings}
          />
        </div>
        <div className="w-full mt-6 flex lg:flex-row flex-col gap-5">
          <AdvancedSettings
            inferenceSettings={inferenceSettings}
            setInferenceSettings={setInferenceSettings}
          />
        </div>
        <OnboardingStep
          onClose={() => {
            dispatchOnboarding({ type: "CLOSE_ONBOARDING" });
          }}
          isOpen={
            onboardingState.isOpen && onboardingState.currentStepIndex === 2
          }
          content="Click to generate your AI cover"
          position="top"
        >
          <div className="mt-6 xl:w-1/2 w-full mx-auto">
            <InferenceButton
              selectedModels={selectedModels}
              uploadedFiles={uploadedFiles}
              setErrorMessage={setErrorMessage}
              inferenceSettings={inferenceSettings}
              isUploading={isUploading}
              dispatchOnboarding={dispatchOnboarding}
            />
          </div>
        </OnboardingStep>
        {!!errorMessage && (
          <div className="text-red-light text-center mt-4 line-clamp-2">
            {errorMessage}
          </div>
        )}
        {!!selectedModels[0] && <FAQ questions={QUESTIONS} />}
      </div>
    </>
  );
};

export default RVCInference;
